import { useState } from 'react';
import { ElementFactory, findNearestConnectionPoint, createThemedElementFactory, getEdgePoint } from '../types/elements';
import { getMouseWorld } from '../utils/canvasUtils';
import { useTheme } from './useTheme';
import { createArchimateElement } from '../factories/archimateElementFactory';
import { ARCHIMATE_ELEMENTS } from '../types/archimateElements';

/**
 * Returns orthogonal pathPoints for a long arrow when its angular deviation
 * from a straight vertical line stays within 18 degrees.
 */
export const computeAutoBendPoints = (x1, y1, x2, y2) => {
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);
  if (dy <= 60) return null;
  const angleFromVerticalDeg = Math.atan2(dx, dy) * (180 / Math.PI);
  if (angleFromVerticalDeg > 18) return null;
  const midY = (y1 + y2) / 2;
  return [
    { x: x1, y: y1 },
    { x: x1, y: midY },
    { x: x2, y: midY },
    { x: x2, y: y2 },
  ];
};

export const applyAutoBendToArrow = (arrow) => {
  const autoPts = computeAutoBendPoints(arrow.x, arrow.y, arrow.x2, arrow.y2);
  return autoPts ? { ...arrow, pathPoints: autoPts } : arrow;
};

// Хук для управления рисованием элементов
export const useDrawing = (canvasRef, zoom, elements, assignZ, incrementZCounter) => {
  const { theme } = useTheme();
  const [tool, setTool] = useState('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [previewElement, setPreviewElement] = useState(null);
  const [drawingStart, setDrawingStart] = useState(null);
  const [isCreatingArrowFromConnection, setIsCreatingArrowFromConnection] = useState(false);

  // Тематизированный фабричный объект
  const ThemedElementFactory = createThemedElementFactory(theme);

  const handleCanvasMouseDown = (e, recordHistory) => {
    const { x, y } = getMouseWorld(e, canvasRef, zoom);
    setIsDrawing(true);
    setDrawingStart({ x, y });

    let preview = null;
    switch (tool) {
      case 'rectangle':
        preview = ThemedElementFactory.rectangle(x, y, 0, 0);
        break;
      case 'note':
        preview = ThemedElementFactory.note(x, y, 0, 0);
        break;
      case 'circle':
        preview = ThemedElementFactory.circle(x, y, 0);
        break;
      case 'arrow':
        preview = ThemedElementFactory.arrow(x, y, x, y);
        break;
      case 'text': {
        const txt = assignZ(ThemedElementFactory.text(x, y));
        incrementZCounter();
        recordHistory('Текст');
        setTool('select');
        return { addElement: txt };
      }

      // Обработка всех новых типов фигур через фабрику
      default:
        // Если это один из C4 типов, он обрабатывается ниже
        if (tool.startsWith('c4-')) break;

        // Проверяем, есть ли такой метод в фабрике для новых фигур
        if (ThemedElementFactory[tool]) {
             // Для INTERFACE используем логику как для круга, если он там определен с радиусом?
             // В elements.js я определил INTERFACE как createElements(..., { radius: 20 }).
             // Но он не создает generic shape с 0,0.
             // Давайте проверим factory.

             if (tool === 'interface') {
                 preview = ThemedElementFactory[tool](x, y); // Фиксированный радиус
             } else {
                 // Для остальных геометрических фигур
                 preview = ThemedElementFactory[tool](x, y);
                 // Переопределяем размеры в 0 для рисования перетаскиванием
                 preview.width = 0;
                 preview.height = 0;
             }
        }
        break;

      // Палитра C4: создаём превью прямоугольника с фиксированным размером и стилем.
      // ВАЖНО: тип элемента оставляем "rectangle", а C4-тип храним в meta/c4,
      // чтобы он корректно работал со всей существующей логикой Canvas (drag, resize, текст и т.п.).
      case 'c4-person':
      case 'c4-external-person':
      case 'c4-system':
      case 'c4-external-system':
      case 'c4-container':
      case 'c4-database':
      case 'c4-queue':
      case 'c4-webapp':
      case 'c4-mobileapp':
      case 'c4-filestore':
      case 'c4-microservice':
      case 'c4-serverless':
      case 'c4-component':
      case 'c4-controller':
      case 'c4-repository':
      case 'c4-service':
      case 'c4-facade':
      case 'c4-class':
      case 'c4-interface':
      case 'c4-enum':
      case 'c4-abstract-class': {
        // Определяем kind — часть после "c4-"
        const kind = tool.replace('c4-', '');

        // Цвета для каждого уровня C4
        const C4_PRESETS = {
          // Context
          'person':          { fill: '#083F88', stroke: '#073578', text: '#fff', label: 'Person',          w: 120, h: 130 },
          'external-person': { fill: '#6B6B6B', stroke: '#5C5C5C', text: '#fff', label: 'External Person', w: 120, h: 130 },
          'system':          { fill: '#1168BD', stroke: '#0E5AA7', text: '#fff', label: 'System',          w: 220, h: 120 },
          'external-system': { fill: '#999999', stroke: '#8A8A8A', text: '#fff', label: 'External System', w: 220, h: 120 },
          // Container
          'container':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Container',       w: 220, h: 120 },
          'database':        { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Database',        w: 140, h: 130 },
          'queue':           { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Message Queue',   w: 220, h: 100 },
          'webapp':          { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Web App',         w: 220, h: 120 },
          'mobileapp':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Mobile App',      w: 140, h: 140 },
          'filestore':       { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'File Store',      w: 220, h: 100 },
          'microservice':    { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Microservice',    w: 200, h: 120 },
          'serverless':      { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: 'Serverless Fn',   w: 200, h: 120 },
          // Component
          'component':       { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', label: 'Component',       w: 200, h: 100 },
          'controller':      { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', label: 'Controller',      w: 200, h: 100 },
          'repository':      { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', label: 'Repository',      w: 200, h: 100 },
          'service':         { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', label: 'Service',         w: 200, h: 100 },
          'facade':          { fill: '#85BBF0', stroke: '#78AAD4', text: '#000', label: 'Facade',          w: 200, h: 100 },
          // Code
          'class':           { fill: '#C9E8FF', stroke: '#5A9FD4', text: '#000', label: 'Class',           w: 180, h: 100 },
          'interface':       { fill: '#C9E8FF', stroke: '#5A9FD4', text: '#000', label: 'Interface',       w: 180, h: 100 },
          'enum':            { fill: '#C9E8FF', stroke: '#5A9FD4', text: '#000', label: 'Enum',            w: 180, h: 100 },
          'abstract-class':  { fill: '#C9E8FF', stroke: '#5A9FD4', text: '#000', label: 'Abstract Class',  w: 180, h: 100 },
        };
        const preset = C4_PRESETS[kind] || { fill: '#438DD5', stroke: '#3A7BB8', text: '#fff', label: kind, w: 160, h: 100 };
        const w = preset.w, h = preset.h;
        preview = ThemedElementFactory.rectangle(x - w / 2, y - h / 2, w, h, {
          fill: preset.fill,
          stroke: preset.stroke,
          strokeWidth: 2,
          borderRadius: 8,
          c4: { kind },
          defaultLabel: preset.label,
        });
        // Для Canvas тип оставляем "rectangle". C4-метаданные храним отдельно.
        // Это позволяет использовать уже существующие обработчики connection points, drag, resize и текста.
        // Ensure meta object exists and contains basic c4-meta
        preview.meta = preview.meta || {};
        preview.meta.c4Type = kind;
        preview.meta.description = preview.meta.description || '';
        preview.meta.technology = preview.meta.technology || '';
        // Keep text color and defaultLabel for later use
        preview.defaultLabel = preset.label;
        preview.textColor = preset.text;
        break;
      }

      // BCM: Domain (L1 container block)
      case 'bcm-domain': {
        const preset = { fill: '#EFF6FF', stroke: '#2563EB', text: '#1E40AF', label: 'Business Domain', w: 420, h: 320 };
        preview = ThemedElementFactory.rectangle(
          x - preset.w / 2, y - preset.h / 2, preset.w, preset.h,
          {
            fill: preset.fill,
            stroke: preset.stroke,
            strokeWidth: 2,
            borderRadius: 4,
            bcm: { level: 'domain', maturity: 0, owner: '', status: 'active' },
          }
        );
        preview.content = preset.label;
        preview.textColor = preset.text;
        break;
      }

      // BCM: Capability (L2)
      case 'bcm-capability': {
        const preset = { fill: '#FFFFFF', stroke: '#94A3B8', text: '#1E293B', label: 'Capability', w: 180, h: 90 };
        preview = ThemedElementFactory.rectangle(
          x - preset.w / 2, y - preset.h / 2, preset.w, preset.h,
          {
            fill: preset.fill,
            stroke: preset.stroke,
            strokeWidth: 1,
            borderRadius: 4,
            bcm: { level: 'capability', maturity: 0, owner: '', status: 'active' },
          }
        );
        preview.content = preset.label;
        preview.textColor = preset.text;
        break;
      }

      // BCM: Sub-capability (L3)
      case 'bcm-subcapability': {
        const preset = { fill: '#F8FAFC', stroke: '#CBD5E1', text: '#475569', label: 'Sub-capability', w: 150, h: 60 };
        preview = ThemedElementFactory.rectangle(
          x - preset.w / 2, y - preset.h / 2, preset.w, preset.h,
          {
            fill: preset.fill,
            stroke: preset.stroke,
            strokeWidth: 1,
            borderRadius: 3,
            bcm: { level: 'subcapability', maturity: 0, owner: '', status: 'active' },
          }
        );
        preview.content = preset.label;
        preview.textColor = preset.text;
        break;
      }
    }

    // Обработка ArchiMate элементов
    if (tool.startsWith('archimate-') && !preview) {
      const archimateType = tool.replace('archimate-', '');
      if (ARCHIMATE_ELEMENTS[archimateType]) {
        const w = 140, h = 70;
        preview = createArchimateElement(archimateType, x - w / 2, y - h / 2, w, h);
      }
    }

    setPreviewElement(preview);
    return null;
  };

  const handleCanvasMouseMove = (e) => {
    if (isCreatingArrowFromConnection) {
      handleArrowFromConnectionMouseMove(e);
      return;
    }
    if (!isDrawing || !previewElement || !drawingStart) return;

    const { x: cx, y: cy } = getMouseWorld(e, canvasRef, zoom);
    let upd = { ...previewElement };

    if (tool === 'rectangle' || tool === 'note') {
      const w = Math.abs(cx - drawingStart.x);
      const h = Math.abs(cy - drawingStart.y);
      upd = { ...upd, x: Math.min(cx, drawingStart.x), y: Math.min(cy, drawingStart.y), width: w, height: h };
    } else if (tool === 'circle') {
      const r = Math.hypot(cx - drawingStart.x, cy - drawingStart.y);
      upd = { ...upd, x: drawingStart.x - r, y: drawingStart.y - r, radius: r };
    } else if (tool === 'arrow') {
      const conn = findNearestConnectionPoint(cx, cy, elements);
      upd = { ...upd, x2: conn ? conn.point.x : cx, y2: conn ? conn.point.y : cy };
    } else if (ThemedElementFactory[tool] && !tool.startsWith('c4-')) {
        // Общая логика для всех новых фигур (как rectangle)
        // Исключаем circle, который выше
        if (tool === 'interface') {
             // Interface (круг) - можно сделать drag для radius, или фиксированный
             // Если фиксированный, просто перемещаем
             upd = { ...upd, x: cx, y: cy };
        } else {
             const w = Math.abs(cx - drawingStart.x);
             const h = Math.abs(cy - drawingStart.y);
             upd = { ...upd, x: Math.min(cx, drawingStart.x), y: Math.min(cy, drawingStart.y), width: w, height: h };
        }
    } else if (tool.startsWith('c4-')) {
      // C4 блоки фиксированного размера — просто центрируем превью под курсором
      const w = previewElement.width || 160;
      const h = previewElement.height || 100;
      upd = { ...upd, x: cx - w / 2, y: cy - h / 2 };
    } else if (tool.startsWith('archimate-')) {
      // ArchiMate элементы — центрируем превью под курсором
      const w = previewElement.width || 140;
      const h = previewElement.height || 70;
      upd = { ...upd, x: cx - w / 2, y: cy - h / 2 };
    } else if (tool.startsWith('bcm-')) {
      // BCM blocks are fixed size — center preview under cursor
      const w = previewElement.width || 180;
      const h = previewElement.height || 90;
      upd = { ...upd, x: cx - w / 2, y: cy - h / 2 };
    }
    setPreviewElement(upd);
  };

  // Шаблоны текста для C4 элементов (1–3 строки в нотации C4)
  const getDefaultC4Text = (kind) => {
    switch (kind) {
      case 'person':
        return 'User\n[Person]\nDescription of the user';
      case 'external-person':
        return 'External Person\n[External Person]\nExternal user or system';
      case 'system':
        return 'Software System\n[Software System]\nDescription of the system';
      case 'external-system':
        return 'External System\n[External System]\nExternal software system';
      case 'container':
        return 'Container\n[Container: Technology]\nDescription of the container';
      case 'database':
        return 'Database\n[Container: Database]\nStores data';
      case 'queue':
        return 'Message Queue\n[Container: Queue]';
      case 'webapp':
        return 'Web Application\n[Container: Web App]\nWeb application';
      case 'mobileapp':
        return 'Mobile App\n[Container: Mobile]\nMobile application';
      case 'filestore':
        return 'File Storage\n[Container: Storage]';
      case 'microservice':
        return 'Microservice\n[Container: Microservice]\nService responsibility';
      case 'serverless':
        return 'Serverless Function\n[Container: Serverless]\nExecutes function';
      case 'component':
        return 'Component\n[Component]\nImplementation detail';
      case 'controller':
        return 'Controller\n[Component]\nHandles requests';
      case 'repository':
        return 'Repository\n[Component]\nPersists data';
      case 'service':
        return 'Service\n[Component]\nBusiness logic';
      case 'facade':
        return 'Facade\n[Component]\nSimplified interface';
      case 'class':
        return 'Class\n«class»\nDescription';
      case 'interface':
        return 'Interface\n«interface»\nDescription';
      case 'enum':
        return 'Enum\n«enum»\nDescription';
      case 'abstract-class':
        return 'Abstract Class\n«abstract»\nDescription';
      default:
        return 'Element\n[C4]\nDescription';
    }
  };

  const finalizePreview = (recordHistory) => {
    if (!isDrawing || !previewElement) return null;
    let add = false;
    let el = { ...previewElement };

    if (tool === 'rectangle' || tool === 'note') {
      add = el.width > 10 && el.height > 10;
    } else if (tool === 'circle') {
      add = el.radius > 5;
    } else if (tool === 'arrow') {
      const dist = Math.hypot(el.x2 - el.x, el.y2 - el.y);
      if (dist > 10) {
        add = true;
        const sc = findNearestConnectionPoint(el.x, el.y, elements);
        const ec = findNearestConnectionPoint(el.x2, el.y2, elements);
        if (sc) {
          el.startConnection = { elementId: sc.element.id, side: sc.point.side };
          // Используем точку на границе элемента (без отступа)
          const edgePoint = getEdgePoint(sc.element, sc.point.side);
          el.x = edgePoint.x; el.y = edgePoint.y;
        }
        if (ec) {
          el.endConnection = { elementId: ec.element.id, side: ec.point.side };
          // Используем точку на границе элемента (без отступа)
          const edgePoint = getEdgePoint(ec.element, ec.point.side);
          el.x2 = edgePoint.x; el.y2 = edgePoint.y;
        }
        el = applyAutoBendToArrow(el);
      }
    } else if (tool.startsWith('c4-')) {
      add = true; // фиксированный размер, всегда добавляем
    } else if (tool.startsWith('archimate-')) {
      add = true; // ArchiMate элементы фиксированного размера, всегда добавляем
    } else if (tool.startsWith('bcm-')) {
      add = true; // BCM элементы фиксированного размера, всегда добавляем
    } else if (ThemedElementFactory[tool]) {
        // Поддержка остальных фигур
        const w = el.width ?? 0;
        const h = el.height ?? 0;

        if (w < 10 && h < 10) {
             // interface определяется через radius, нужно проверить
             if (tool === 'interface') {
                 // interface имеет radius
                 if (!el.radius || el.radius < 5) {
                     const def = ThemedElementFactory[tool](el.x, el.y);
                     el.radius = def.radius ?? 20;
                 }
                 add = el.radius > 5;
             } else {
                 const def = ThemedElementFactory[tool](el.x, el.y);
                 el.width = def.width ?? 100;
                 el.height = def.height ?? 60;
                 // Центрируем относительно точки клика
                 el.x = el.x - el.width / 2;
                 el.y = el.y - el.height / 2;
                 add = true;
             }
        } else {
             // Обычная проверка для drawn shapes
             if (el.width !== undefined && el.height !== undefined) {
                  add = el.width > 10 && el.height > 10;
             } else if (el.radius !== undefined) {
                  add = el.radius > 5;
             } else {
                  add = true;
             }
        }
    }

    setIsDrawing(false);
    setPreviewElement(null);
    setDrawingStart(null);
    setTool('select');

    if (add) {
      const withZ = assignZ(el);
      incrementZCounter();
      recordHistory('Создание');

      // Для C4 — автоматически создаём подпись в центре (1–3 строки в нотации C4)
      if (tool.startsWith('c4-')) {
        const cx = withZ.x + withZ.width / 2;
        const cy = withZ.y + withZ.height / 2;
        const kind = withZ.c4?.kind || withZ.meta?.c4Type || tool.replace('c4-', '');
        const defaultText = getDefaultC4Text(kind);
        const t = assignZ(ThemedElementFactory.text(cx, cy, defaultText));
        t.attachedToElement = withZ.id;
        // Use C4 preset text color (white for dark elements, black for light ones)
        if (withZ.textColor) t.fill = withZ.textColor;
        incrementZCounter();
        return [withZ, t];
      }

      return withZ;
    }
    return null;
  };

  const handleStartArrowFromConnection = (elementId, side, point) => {
    setIsCreatingArrowFromConnection(true);
    // Находим элемент и получаем точку на его границе
    const startElement = elements.find(el => el.id === elementId);
    const edgePoint = startElement ? getEdgePoint(startElement, side) : { x: point.x, y: point.y };
    const pr = ElementFactory.arrow(edgePoint.x, edgePoint.y, edgePoint.x, edgePoint.y);
    pr.startConnection = { elementId, side };
    setPreviewElement(pr);
    setIsDrawing(true);
    setDrawingStart({ x: edgePoint.x, y: edgePoint.y });
  };

  const handleArrowFromConnectionMouseMove = (e) => {
    if (!isCreatingArrowFromConnection || !previewElement) return;
    const { x, y } = getMouseWorld(e, canvasRef, zoom);
    const conn = findNearestConnectionPoint(x, y, elements);
    if (conn) {
      // Используем точку на границе элемента
      const edgePoint = getEdgePoint(conn.element, conn.point.side);
      setPreviewElement(prev => prev ? { ...prev, x2: edgePoint.x, y2: edgePoint.y } : prev);
    } else {
      setPreviewElement(prev => prev ? { ...prev, x2: x, y2: y } : prev);
    }
  };

  const handleArrowFromConnectionMouseUp = (recordHistory) => {
    if (!isCreatingArrowFromConnection || !previewElement) return null;
    const dist = Math.hypot(previewElement.x2 - previewElement.x, previewElement.y2 - previewElement.y);
    setIsCreatingArrowFromConnection(false);
    setPreviewElement(null);
    setIsDrawing(false);
    if (dist > 10) {
      const endConn = findNearestConnectionPoint(previewElement.x2, previewElement.y2, elements);
      let arrow = { ...previewElement };
      if (endConn) {
        arrow.endConnection = { elementId: endConn.element.id, side: endConn.point.side };
        // Используем точку на границе элемента
        const edgePoint = getEdgePoint(endConn.element, endConn.point.side);
        arrow.x2 = edgePoint.x;
        arrow.y2 = edgePoint.y;
      }
      arrow = assignZ(applyAutoBendToArrow(arrow));
      incrementZCounter();
      return arrow;
    }
    return null;
  };

  return {
    tool,
    setTool,
    isDrawing,
    previewElement,
    isCreatingArrowFromConnection,
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    finalizePreview,
    handleStartArrowFromConnection,
    handleArrowFromConnectionMouseMove,
    handleArrowFromConnectionMouseUp
  };
};
